import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { Box } from "../Box/Box.mjs";
import { pincode, pincodeSlotWrapper, pincodeDefault, pincodeBreakpoint, pincodeMessages, fieldMessage } from "./Pincode.css.mjs";
import assert from "assert";
import { Stack } from "../Stack/Stack.mjs";
import { FieldMessage } from "../FieldMessage/FieldMessage.mjs";
import { TextLink } from "../TextLink/TextLink.mjs";
import { Text } from "../Text/Text.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconQuestionCircle } from "../icons/IconQuestionCircle/IconQuestionCircle.mjs";
import { Field } from "../private/Field/Field.mjs";
const validPatterns = {
  alpha: `[a-zA-Z]`,
  numeric: `[0-9]`,
  alphanumeric: `[a-zA-Z0-9]`
};
const validInputModes = {
  alpha: "text",
  numeric: "numeric",
  alphanumeric: "text"
};
const Pincode = ({
  length,
  id,
  onComplete,
  pattern = "alphanumeric",
  message,
  helpText,
  helpLink,
  ...restProps
}) => {
  assert(length >= 1 && length <= 6, "pincode must have 1 to 6 slots");
  const [pin, setPin] = useState(Array(length).fill(""));
  const inputsRef = useRef([]);
  const messageId = `${id}-message`;
  useEffect(() => {
    const value = pin.join("");
    if (value.length === length) {
      onComplete(value);
    }
  }, [pin]);
  const validateChar = (char) => {
    const regex = new RegExp(validPatterns[pattern]);
    return regex.test(char);
  };
  const validateString = (string, clipboardLength) => {
    const regex = new RegExp(validPatterns[pattern].concat(`{${clipboardLength}}`));
    return regex.test(string);
  };
  const inputHandler = (e, idx) => {
    if (!e.target.value || validateChar(e.target.value)) {
      const newPin = [...pin];
      newPin[idx] = e.target.value;
      setPin(newPin);
    }
    if (idx < length - 1 && e.target.value && validateChar(e.target.value)) {
      const nextSlot = inputsRef.current[idx + 1];
      nextSlot == null ? void 0 : nextSlot.focus();
    }
  };
  const backspaceHandler = (e, idx) => {
    const currentSlot = e.target;
    if (idx > 0 && e.key === "Backspace" && !currentSlot.value) {
      const prevSlot = inputsRef.current[idx - 1];
      prevSlot == null ? void 0 : prevSlot.focus();
    }
  };
  const pasteHandler = (e, idx) => {
    e.preventDefault();
    let clipboardPin = e.clipboardData.getData("Text");
    clipboardPin = clipboardPin.split(" ").join("");
    if (clipboardPin.length <= length && validateString(clipboardPin, clipboardPin.length)) {
      const newPin = [...pin];
      const clipboardPinArray = clipboardPin.split("");
      clipboardPinArray.forEach((char, i) => {
        if (!validateChar(char) && idx + i < length) {
          newPin[idx + i] = " ";
        } else if (idx + i < length) {
          newPin[idx + i] = char;
        }
      });
      setPin(newPin);
      if (idx + clipboardPinArray.length < length) {
        const nextSlot = inputsRef.current[idx + clipboardPinArray.length];
        nextSlot == null ? void 0 : nextSlot.focus();
      } else {
        const currentSlot = inputsRef.current[idx];
        currentSlot == null ? void 0 : currentSlot.blur();
      }
    }
  };
  return /* @__PURE__ */ jsxs(Stack, { space: "xxsmall", children: [
    /* @__PURE__ */ jsx(Box, { className: pincode, children: pin.map((slot, idx) => /* @__PURE__ */ jsx(
      Box,
      {
        className: [pincodeSlotWrapper, length < 5 ? pincodeDefault : pincodeBreakpoint],
        children: /* @__PURE__ */ jsx(Field, { id, ...restProps, children: (overlays, fieldProps) => /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              component: "input",
              maxLength: 1,
              textAlign: "center",
              value: slot,
              pattern,
              inputMode: validInputModes[pattern],
              autoComplete: "one-time-code",
              onChange: (e) => inputHandler(e, idx),
              onKeyDown: (e) => backspaceHandler(e, idx),
              onPaste: (e) => pasteHandler(e, idx),
              ...fieldProps,
              ref: (element) => {
                inputsRef.current[idx] = element;
              }
            }
          ),
          overlays
        ] }) })
      },
      idx
    )) }),
    /* @__PURE__ */ jsx(Fragment, { children: (message || helpText) && /* @__PURE__ */ jsxs(Box, { className: pincodeMessages, children: [
      message && /* @__PURE__ */ jsx(Box, { className: fieldMessage, children: /* @__PURE__ */ jsx(FieldMessage, { id: messageId, tone: restProps.tone, message }) }),
      helpText && helpLink && /* @__PURE__ */ jsx(Box, { display: "flex", flexWrap: "wrap", children: /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(TextLink, { "aria-label": "help", icon: /* @__PURE__ */ jsx(IconQuestionCircle, {}), href: helpLink, children: helpText }) }) })
    ] }) })
  ] });
};
Pincode.displayName = "Pincode";
export {
  Pincode
};
